import { StateCreator } from 'zustand';
import { MyCartActionButtonsEnum } from '@/types/action-buttons/action-buttons';
import { LocalMyCart } from '@/types/my-cart/local-my-cart';
import { SimpleProductInfo } from '@/types/products/simple-product-info';
import { WithQuantity } from '@/types/products/with-quantity';
import { priceCheck } from '@/utils/price-check';

export type MyCartPendingOperation = {
  status: 'pending';
  buttonType: MyCartActionButtonsEnum | undefined;
  operationId: string;
};

export type MyCartSlice = {
  myCart: LocalMyCart;
  setMyCart: (value: LocalMyCart) => void;
  decreaseProductQuantity: (product: WithQuantity<SimpleProductInfo>) => void;
  increaseProductQuantity: (product: WithQuantity<SimpleProductInfo>) => void;
  setProductQuantity: (product: WithQuantity<SimpleProductInfo>, quantity: number) => void;
  addProductToMyCart: (product: WithQuantity<SimpleProductInfo>) => void;
  removeProductFromMyCart: (product: WithQuantity<SimpleProductInfo>) => void;
  resetMyCart: () => void;
  isValidVoucher: boolean;
  setIsValidVoucher: (isValid: boolean) => void;
  setMyCartVoucherValue: (value: string) => void;
  pendingByProductId: Record<string, MyCartPendingOperation>;
  markMyCartPending: (
    productId: string,
    buttonType: MyCartActionButtonsEnum | undefined,
    operationId: string,
  ) => void;
  clearMyCartPending: (productId: string, operationId?: string) => void;
  getMyCartPending: (productId: string) => MyCartPendingOperation | undefined;
  isMyCartPending: (productId: string, buttonType?: MyCartActionButtonsEnum) => boolean;
  resetMyCartPending: () => void;
  isPendingUpdateMyCartProductId: string | undefined;
  setIsPendingUpdateMyCartProductId: (id: string | undefined) => void;
  actionButtonType: MyCartActionButtonsEnum | undefined;
  setActionButtonType: (buttonType: MyCartActionButtonsEnum | undefined) => void;
};

const defaultValue: LocalMyCart = {
  products: [],
  amount: 0,
  discountedAmount: 0,
  totalAmount: 0,
  voucher: '',
};

export const createMyCartSlice: StateCreator<MyCartSlice> = (set, get) => ({
  myCart: defaultValue,
  isValidVoucher: false,
  pendingByProductId: {},
  markMyCartPending: (productId, buttonType, operationId) =>
    set(state => ({
      pendingByProductId: {
        ...state.pendingByProductId,
        [productId]: {
          status: 'pending',
          buttonType,
          operationId,
        },
      },
    })),
  clearMyCartPending: (productId, operationId) =>
    set(state => {
      const pendingOperation = state.pendingByProductId[productId];

      if (!pendingOperation || (operationId && pendingOperation.operationId !== operationId)) {
        return {};
      }

      const pendingByProductId = { ...state.pendingByProductId };
      delete pendingByProductId[productId];

      return { pendingByProductId };
    }),
  getMyCartPending: productId => get().pendingByProductId[productId],
  isMyCartPending: (productId, buttonType) => {
    const pendingOperation = get().pendingByProductId[productId];

    if (!pendingOperation) {
      return false;
    }

    if (!buttonType) {
      return true;
    }

    return pendingOperation.buttonType === buttonType;
  },
  resetMyCartPending: () => set(() => ({ pendingByProductId: {} })),
  actionButtonType: undefined,
  setActionButtonType: (buttonType: MyCartActionButtonsEnum | undefined) =>
    set(() => ({ actionButtonType: buttonType })),
  setMyCart: (value: LocalMyCart) =>
    set(state => ({ myCart: { ...value, voucher: state.myCart.voucher } })),
  setIsValidVoucher: isValid => set(() => ({ isValidVoucher: isValid })),
  isPendingUpdateMyCartProductId: undefined,
  setIsPendingUpdateMyCartProductId: (id: string | undefined) =>
    set(() => ({ isPendingUpdateMyCartProductId: id })),
  setMyCartVoucherValue: value => set(state => ({ myCart: { ...state.myCart, voucher: value } })),
  resetMyCart: () =>
    set(() => ({
      myCart: defaultValue,
      isValidVoucher: false,
      pendingByProductId: {},
    })),
  increaseProductQuantity: product =>
    set(state => {
      const newAmount = state.myCart.amount + product.price;
      const newDiscountedAmount = state.myCart.discountedAmount + product.discountedPrice;
      const newTotalAmount = state.myCart.totalAmount + product.discountedPrice;

      let products = [...state.myCart.products];

      const addExisitngProduct = () => {
        products = state.myCart.products.map(p =>
          p.id === product.id ? { ...p, quantity: p.quantity + 1 } : p,
        );
      };

      addExisitngProduct();

      return {
        myCart: {
          products: products,
          amount: newAmount,
          discountedAmount: newDiscountedAmount,
          totalAmount: newTotalAmount,
          voucher: state.myCart.voucher,
        },
      };
    }),
  decreaseProductQuantity: product =>
    set(state => {
      const newAmount = state.myCart.amount - product.price;
      const newDiscountedAmount = state.myCart.discountedAmount - product.discountedPrice;
      const newTotalAmount = state.myCart.totalAmount - product.discountedPrice;

      let products = [...state.myCart.products];

      const removeExisitngProduct = () => {
        products = state.myCart.products.map(p =>
          p.id === product.id ? { ...p, quantity: p.quantity - 1 } : p,
        );
      };

      removeExisitngProduct();

      return {
        myCart: {
          products: products,
          amount: newAmount,
          discountedAmount: newDiscountedAmount,
          totalAmount: newTotalAmount,
          voucher: state.myCart.voucher,
        },
      };
    }),
  setProductQuantity: (product, quantity) =>
    set(state => {
      let products = [...state.myCart.products];

      if (quantity === 0) {
        products = products.filter(p => p.id !== product.id);
      } else {
        const productExists = products.find(p => p.id === product.id);

        if (productExists) {
          products = products.map(p => (p.id === product.id ? { ...p, quantity: quantity } : p));
        } else {
          products.push({ ...product, quantity });
        }
      }

      const newAmount = products.reduce((acc, p) => acc + p.price * p.quantity, 0);
      const newDiscountedAmount = products.reduce(
        (acc, p) => acc + p.discountedPrice * p.quantity,
        0,
      );
      const newTotalAmount = newDiscountedAmount;

      return {
        myCart: {
          products: products,
          amount: newAmount,
          discountedAmount: newDiscountedAmount,
          totalAmount: newTotalAmount,
        },
      };
    }),
  addProductToMyCart: product =>
    set(state => {
      const newAmount = state.myCart.amount + product.price * product.quantity;
      const newDiscountedAmount =
        state.myCart.discountedAmount + product.discountedPrice * product.quantity;
      const newTotalAmount = state.myCart.totalAmount + product.discountedPrice * product.quantity;

      const isProductInMyCart = state.myCart.products.find(p => p.id === product.id);

      let products = [...state.myCart.products];

      const addExisitngProduct = () => {
        products = state.myCart.products.map(p =>
          p.id === product.id ? { ...p, quantity: p.quantity + product.quantity } : p,
        );
      };

      const addNewProduct = () => {
        products = [...state.myCart.products, product];
      };

      if (isProductInMyCart) {
        addExisitngProduct();
      } else {
        addNewProduct();
      }

      return {
        myCart: {
          products: products,
          amount: priceCheck(newAmount),
          discountedAmount: priceCheck(newDiscountedAmount),
          totalAmount: priceCheck(newTotalAmount),
          voucher: state.myCart.voucher,
        },
      };
    }),
  removeProductFromMyCart: product =>
    set(state => {
      const newAmount = state.myCart.amount - product.price * product.quantity;
      const newDiscountedAmount =
        state.myCart.discountedAmount - product.discountedPrice * product.quantity;
      const newTotalAmount = state.myCart.totalAmount - product.discountedPrice * product.quantity;

      return {
        myCart: {
          products: state.myCart.products.filter(p => p.id !== product.id),
          amount: priceCheck(newAmount),
          discountedAmount: priceCheck(newDiscountedAmount),
          totalAmount: priceCheck(newTotalAmount),
          voucher: state.myCart.voucher,
        },
      };
    }),
});
